"use client"

import { useState, useEffect, useRef } from "react"
import VoteDetails from "./VoteDetails"
import TypeSelector from "./TypeSelector"
import { ChevronDown, Pencil } from "lucide-react"

import "./PlaceCard.css"

const PlaceCard = ({
  place,
  onSwipe,
  isActive,
  onPlaceUpdate,
  voteData = null,
  myVote = null,
  onSideClick,
  currentIndex,
  totalPlaces,
  allVotes,
  userId,
  showVotes = false,
  onVoteChange,
  isLastItem = false,
  placeType = null,
  onTypeChange,
  onNoteChange, // Add this new prop
}) => {
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const [isEditing, setIsEditing] = useState(false)
  const [editedName, setEditedName] = useState(place.name)
  const [currentPlace, setCurrentPlace] = useState(place)
  const inputRef = useRef(null)
  const isBlurSaveRef = useRef(false)
  const hasLoadedEditsRef = useRef(false)
  const [showDetails, setShowDetails] = useState(false)

  // Load saved edits only once when component mounts or place changes
  useEffect(() => {
    const placeKey = `${place.lat},${place.lng}`

    // Reset the flag when place changes
    if (!hasLoadedEditsRef.current || currentPlace.id !== place.id) {
      hasLoadedEditsRef.current = true

      const loadSavedEdit = () => {
        const savedEdits = localStorage.getItem("triptoi-edits")
        if (savedEdits) {
          try {
            const edits = JSON.parse(savedEdits)
            if (edits[placeKey]) {
              const updatedPlace = { ...place, name: edits[placeKey] }
              setCurrentPlace(updatedPlace)
              setEditedName(edits[placeKey])
              if (onPlaceUpdate) {
                onPlaceUpdate(place.id, updatedPlace)
              }
            } else {
              setCurrentPlace(place)
              setEditedName(place.name)
            }
          } catch (error) {
            console.error("Error loading saved edits:", error)
            setCurrentPlace(place)
            setEditedName(place.name)
          }
        } else {
          setCurrentPlace(place)
          setEditedName(place.name)
        }
      }

      loadSavedEdit()
    }
  }, [place.id]) // Only depend on place identity, not the full place object

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select() // Select all text for easy editing
    }
  }, [isEditing])

  const handleNameClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    console.log("Starting edit for:", currentPlace.name)
    setIsEditing(true)
    setEditedName(currentPlace.name)
    isBlurSaveRef.current = false
  }

  const handleEditSave = () => {
    const trimmedName = editedName.trim()

    if (trimmedName && trimmedName !== currentPlace.name) {
      console.log("Saving edit:", currentPlace.name, "->", trimmedName)

      const savedEdits = JSON.parse(localStorage.getItem("triptoi-edits") || "{}")
      const key = `${place.lat},${place.lng}`
      savedEdits[key] = trimmedName
      localStorage.setItem("triptoi-edits", JSON.stringify(savedEdits))

      const updatedPlace = { ...currentPlace, name: trimmedName }
      setCurrentPlace(updatedPlace)

      if (onPlaceUpdate) {
        onPlaceUpdate(place.id, updatedPlace)
      }

      console.log("Edit saved successfully")
    } else if (!trimmedName) {
      setEditedName(currentPlace.name)
    }

    setIsEditing(false)
    isBlurSaveRef.current = false
  }

  const handleEditCancel = () => {
    console.log("Canceling edit")
    setEditedName(currentPlace.name)
    setIsEditing(false)
    isBlurSaveRef.current = false
  }

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault()
      isBlurSaveRef.current = true // Prevent blur from also saving
      handleEditSave()
    } else if (e.key === "Escape") {
      e.preventDefault()
      isBlurSaveRef.current = true // Prevent blur from saving
      handleEditCancel()
    }
  }

  const handleInputBlur = () => {
    // Only save on blur if it wasn't triggered by Enter or Escape
    if (!isBlurSaveRef.current) {
      handleEditSave()
    }
  }

  const handleSaveClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    isBlurSaveRef.current = true
    handleEditSave()
  }

  const handleCancelClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    isBlurSaveRef.current = true
    handleEditCancel()
  }

  const createViewUrl = () => {
    const name = currentPlace.name
    if (name && !name.match(/^Location \d+$/)) {
      return `https://www.google.com/maps/search/${encodeURIComponent(name)}/@${currentPlace.lat},${currentPlace.lng},17z`
    }
    return `https://www.google.com/maps/search/?api=1&query=${currentPlace.lat},${currentPlace.lng}`
  }

  const embedUrl = `https://www.google.com/maps?q=${currentPlace.lat},${currentPlace.lng}&output=embed`

  const handleVote = (voteType) => {
    if (onVoteChange) {
      onVoteChange(voteType)
    }
  }

  const getMyVoteStatus = () => {
    if (!myVote) return null
    if (myVote.vote === "love") return "❤️"
    if (myVote.vote === "like") return "👍"
    if (myVote.vote === "skip") return "👎"
    return null
  }

  const getVoteCounts = () => {
    if (!voteData) {
      return { skips: 0, likes: 0, loves: 0 }
    }

    return {
      skips: voteData.skips || 0,
      likes: voteData.likes || 0,
      loves: voteData.loves || 0,
    }
  }

  // Notes functionality
  const getMyNote = () => {
    if (!voteData?.userDetails) return null
    const myDetail = voteData.userDetails.find((detail) => detail.userId === userId)
    return myDetail?.note || null
  }

  const handleEditNote = (currentNote) => {
    const note = prompt("Edit your note:", currentNote)
    if (note !== null) {
      // User didn't cancel
      if (onNoteChange) {
        onNoteChange(note.trim())
      }
    }
  }

  const voteCounts = getVoteCounts()

  return (
    <div className={`place-card ${isActive ? "active" : ""}`}>
      <div className="map-container">
        <iframe
          src={embedUrl}
          width="100%"
          height="250"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title={`Map of ${currentPlace.name}`}
        />

        <button
          className="btn-view-maps-overlay"
          onClick={() => window.open(createViewUrl(), "_blank")}
          title="View on Google Maps"
        >
          🗺️ View on Google Maps
        </button>
      </div>

      <div className="card-content">
        <div className="place-name-section">
          {isEditing ? (
            <div className="edit-name-container">
              <input
                ref={inputRef}
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                onKeyDown={handleKeyPress}
                onBlur={handleInputBlur}
                className="edit-name-input"
                placeholder="Enter place name..."
              />
              <div className="edit-buttons">
                <button type="button" onClick={handleSaveClick} className="edit-save-btn">
                  ✓
                </button>
                <button type="button" onClick={handleCancelClick} className="edit-cancel-btn">
                  ✕
                </button>
              </div>
            </div>
          ) : (
            <div className="name-display">
              <h3 onClick={handleNameClick} className="editable-name" title="Click to edit">
                {currentPlace.name} <div className="edit-pencil"><Pencil/></div>
              </h3>
            </div>
          )}
          <TypeSelector
            currentType={placeType}
            onTypeChange={(type) => {
              const placeKey = `${place.lat},${place.lng}`
              if (onTypeChange) {
                onTypeChange(placeKey, type)
              }
            }}
            place={place}
          />
        </div>

        <div className="netflix-voting">
          <div className="current-vote">
            {myVote && <span className="vote-status-text">You voted: {getMyVoteStatus()}</span>}
          </div>

          <div className="vote-buttons-netflix">
            <div className="vote-button-container">
              <button
                className={`vote-btn-netflix skip-btn ${myVote?.vote === "skip" ? "active" : ""}`}
                onClick={() => handleVote("skip")}
                title="Not interested"
              >
                👎
              </button>
              {showVotes && <div className="vote-count">{voteCounts.skips}</div>}
            </div>

            <div className="vote-button-container">
              <button
                className={`vote-btn-netflix like-btn ${myVote?.vote === "like" ? "active" : ""}`}
                onClick={() => handleVote("like")}
                title="I like this"
              >
                👍
              </button>
              {showVotes && <div className="vote-count">{voteCounts.likes}</div>}
            </div>

            <div className="vote-button-container">
              <button
                className={`vote-btn-netflix love-btn ${myVote?.vote === "love" ? "active" : ""}`}
                onClick={() => handleVote("love")}
                title="I love this!"
              >
                ❤️
              </button>
              {showVotes && <div className="vote-count">{voteCounts.loves}</div>}
            </div>
          </div>

          {/* Collapsible vote details and notes section */}
          <div className="vote-details-section">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="toggle-details-btn"
              title={showDetails ? "Hide votes and notes" : "Show votes and notes"}
            >
              <span className="toggle-text">
                {showDetails ? "Hide" : "Show"} votes & notes
              </span>
              <span className={`toggle-arrow ${showDetails ? "expanded" : ""}`}>
              <ChevronDown />
              </span>
            </button>

            {showDetails && (
              <div className="details-content">
                <VoteDetails voteData={voteData} isVisible={true} currentUserId={userId} onEditNote={handleEditNote} />
                
                {/* Add note button - only show if user hasn't added a note yet */}
                {!getMyNote() && (
                  <button
                    onClick={() => handleEditNote("")}
                    className="add-note-btn-bottom"
                    title="Add a note about this place"
                  >
                   Add note
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default PlaceCard
